import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import { EnvPaths } from '../helpers/environment';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/users.module.scss';
import { UserObject } from '../helpers/objects/userObject';
import { useAuth } from '../context/AuthContext';
import tableStyles from '../css/table.module.scss';
import Pagination from '../components/pagination';
import EditUserModal from '../components/editUserModal';
import { createPortal } from 'react-dom';
import { formatStatus } from '../helpers/functions';
import { useClickAway } from 'react-use';
import CustomDropdown from '../components/customDropdown';
import CustomSearchInput from '../components/customSearchInput';
import NewUserModal from '../components/newUserModal';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

const Users = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isActionOpen, setIsActionOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [userList, setUserList] = useState<UserObject[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [refetch, setRefetch] = useState(false);
  const { user } = useAuth();
  const [editUser, setEditUser] = useState<UserObject | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [status, setStatus] = useState<{ value: string; label: string }>({ value: 'verified', label: t('users.labels.verified') });
  const [filterStatusOpen, setFilterStatusOpen] = useState<boolean>(false);
  const filterStatusDropRef = useRef<HTMLDivElement>(null);
  const [blocked, setBlocked] = useState<{ value: string; label: string }>({ value: 'notblocked', label: t('users.labels.notBlocked') });
  const [filterBlockedOpen, setFilterBlockedOpen] = useState<boolean>(false);
  const filterBlockedDropRef = useRef<HTMLDivElement>(null);
  const [role, setRole] = useState<{ value: string; label: string }>({ value: 'user', label: t('users.labels.user') });
  const [filterRoleOpen, setFilterRoleOpen] = useState<boolean>(false);
  const filterRoleDropRef = useRef<HTMLDivElement>(null);

  const handleSearch = (value: string) => {
    setPage(1);
    setSearchTerm(value);
  };

  const handleActionClick = (id: string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${EnvPaths.SERVER_URL}/admin/get-users?page=${page}&q=${searchTerm}&role=${role.value}&status=all&blocked=${blocked.value}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const { users, pagination } = response.data;
      setUserList(users);
      setTotalPages(pagination.totalPages);
      return users;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUsers();
    }
  }, [refetch, page, user, searchTerm, role, status, blocked]);

  useEffect(() => {
    setStatus((prev) => ({ ...prev, label: t('users.labels.verified') }));
    setBlocked((prev) => ({ ...prev, label: t('users.labels.notBlocked') }));
    setRole((prev) => ({ ...prev, label: t('users.labels.user') }));
  }, [t, i18next]);

  useClickAway(filterStatusDropRef, () => {
    setFilterStatusOpen(false);
  });

  const filterStatusOptions = [
    { value: 'verified', label: t('users.labels.verified') },
    { value: 'notverified', label: t('users.labels.notVerified') },
  ];

  const handleStatusChange = (item: { value: string; label: string }) => {
    setPage(1);
    setStatus(item);
    setFilterStatusOpen(false);
  };

  useClickAway(filterBlockedDropRef, () => {
    setFilterBlockedOpen(false);
  });

  const filterBlockedOptions = [
    { value: 'blocked', label: t('users.labels.blocked') },
    { value: 'notblocked', label: t('users.labels.notBlocked') },
  ];

  const handleBlockedChange = (item: { value: string; label: string }) => {
    setPage(1);
    setBlocked(item);
    setFilterBlockedOpen(false);
  };

  useClickAway(filterRoleDropRef, () => {
    setFilterRoleOpen(false);
  });

  const filterRoleOptions = [
    { value: 'admin', label: t('users.labels.admin') },
    { value: 'user', label: t('users.labels.user') },
    { value: 'skycop', label: t('users.labels.skycop') },
  ];

  const handleRoleChange = (item: { value: string; label: string }) => {
    setPage(1);
    setRole(item);
    setFilterRoleOpen(false);
  };

  return (
    <>
      {editUser && <EditUserModal user={editUser} isOpen={editModalOpen} setIsOpen={setEditModalOpen} refetch={refetch} setRefetch={setRefetch} />}
      {newModalOpen && <NewUserModal isOpen={newModalOpen} setIsOpen={setNewModalOpen} refetch={refetch} setRefetch={setRefetch} />}
      <main className={globalPageStyle.pageContainer}>
        <div className={globalPageStyle.pageContent}>
          {/*filters*/}
          {user && user.role === 'admin' && (
            <div className={styles.flexRow}>
              <CustomSearchInput onSearch={handleSearch} icon="/assets/images/search-icon.svg" placeholder={t('users.search')} />
              <CustomDropdown ref={filterRoleDropRef} isOpen={filterRoleOpen} onToggle={() => setFilterRoleOpen(!filterRoleOpen)} options={filterRoleOptions} value={role} onChange={handleRoleChange} width="215px" label={t('users.labels.role')} />
              {/* <CustomDropdown ref={filterStatusDropRef} isOpen={filterStatusOpen} onToggle={() => setFilterStatusOpen(!filterStatusOpen)} options={filterStatusOptions} value={status} onChange={handleStatusChange} width="215px" label="Verified" /> */}
              <CustomDropdown ref={filterBlockedDropRef} isOpen={filterBlockedOpen} onToggle={() => setFilterBlockedOpen(!filterBlockedOpen)} options={filterBlockedOptions} value={blocked} onChange={handleBlockedChange} width="215px" label={t('users.labels.blocked')} />
              <button className="primaryButton" onClick={() => setNewModalOpen(true)}>
                {t('users.add')}
              </button>
            </div>
          )}
          <br />
          {/*filters*/}
          {user && user.role === 'admin' ? (
            loading ? (
              <div className="loader" />
            ) : userList.length > 0 ? (
              <div className={styles.body}>
                <div className={styles.usersPageContainer}>
                  {/*Table*/}
                  <div className={tableStyles.tableContainer}>
                    <table className={`${tableStyles.table}`}>
                      <thead className={tableStyles.tableHeader}>
                        <tr>
                          <th>{t('users.avatar')}</th>
                          <th>{t('users.name')}</th>
                          <th>{t('users.email')}</th>
                          <th>{t('users.verified')}</th>
                          <th>{t('users.blocked')}</th>
                          <th>{t('users.role')}</th>
                          <th className={tableStyles.actionColumn}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {userList.map((user, index) => {
                          const avatarToShow = user?.avatarUrl;
                          return (
                            <tr key={index}>
                              <td>{avatarToShow ? <img src={avatarToShow} className={styles.userAvatar} alt="Avatar" /> : <div className={styles.userAvatar}>{user?.firstName?.charAt(0)?.toUpperCase() ?? user?.email?.charAt(0)?.toUpperCase()}</div>}</td>
                              <td>
                                {user?.firstName && (user?.firstName.length > 25 ? user?.firstName?.slice(0, 25) + '...' : user?.firstName)} {user?.lastName && (user?.lastName.length > 25 ? user?.lastName?.slice(0, 25) + '...' : user?.lastName)}
                              </td>
                              <td>{user?.email && (user?.email.length > 50 ? user?.email?.slice(0, 50) + '...' : user?.email)}</td>
                              <td>{user?.isVerified ? formatStatus(t, 'verified') : formatStatus(t, 'notVerified')}</td>
                              <td>{user?.isBlocked ? formatStatus(t, 'blocked') : formatStatus(t, 'notBlocked')}</td>
                              <td>
                                <p>{user?.role ? user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1) : t('users.labels.user')}</p>
                              </td>
                              <td className={tableStyles.actionColumn}>
                                <div className={tableStyles.actionShape} ref={buttonRef}>
                                  <img
                                    src="/assets/images/dots.svg"
                                    alt="dots"
                                    onClick={(e) => {
                                      const buttonElement = e.currentTarget as HTMLDivElement;
                                      handleActionClick(user._id, buttonElement);
                                    }}
                                  />
                                  {isActionOpen === user._id &&
                                    typeof window !== 'undefined' &&
                                    createPortal(
                                      <div
                                        className={`${tableStyles.actionShapeDropdown}`}
                                        style={{
                                          top: `${dropdownPosition.top}px`,
                                          left: `${dropdownPosition.left}px`,
                                        }}
                                      >
                                        <div
                                          onClick={() => {
                                            setEditUser(user);
                                            setEditModalOpen(true);
                                            setIsActionOpen(null);
                                          }}
                                        >
                                          {t('users.editUser')}
                                        </div>
                                      </div>,
                                      document.body
                                    )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/*Table*/}
                  {/*Pagination*/}
                  <Pagination onPrevious={() => setPage(page - 1)} onNext={() => setPage(page + 1)} currentPage={page} totalPages={totalPages} />
                  {/*Pagination*/}
                </div>
              </div>
            ) : (
              <h1>{t('users.noData')}</h1>
            )
          ) : (
            <h1>{t('users.noPermission')}</h1>
          )}
        </div>
      </main>
    </>
  );
};

export default Users;
